import { useCallback, useMemo } from 'react';
import { useGetMicrostoreColumnsPaginatedQuery } from 'data/__generated__';
import { TotemSelect, TotemSelectProps } from 'components/TotemSelect';

type MicrostoreColumnSelectProps = Omit<
    Extract<TotemSelectProps<string>, { isMulti?: false }>,
    'value' | 'onChange'
> & {
    value: string | null;
    onChange: (columnId: string | null) => void;
    width?: string;
};

export const MicrostoreColumnSelect = ({ value, onChange, width, ...props }: MicrostoreColumnSelectProps) => {
    const { data: columnsData, refetch } = useGetMicrostoreColumnsPaginatedQuery({
        variables: {
            offset: 0,
            filters: {
                searchValue: value || '',
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const options = useMemo(
        () =>
            (columnsData?.microstoreColumnsFilter.microstoreColumns || []).map((column) => ({
                label: `${column.siteName} - ${column.name}`,
                value: column._id,
            })),
        [columnsData],
    );

    // Server-side search: fetch and return the matching columns so TotemSelect can display
    // them (async mode renders the fetched results, not the `options` prop). Returning []
    // here was the bug — typing emptied the list.
    const fetchOptions = useCallback(
        async (inputValue: string): Promise<{ label: string; value: string }[]> => {
            const result = await refetch({ offset: 0, filters: { searchValue: inputValue } });
            return (result.data?.microstoreColumnsFilter.microstoreColumns || []).map((column) => ({
                label: `${column.siteName} - ${column.name}`,
                value: column._id,
            }));
        },
        [refetch],
    );

    return (
        <div className="min-w-[450px]" style={{ width: width ?? '450px' }}>
            <TotemSelect<string>
                isClearable
                placeholder="Sélectionner une colonne"
                value={value}
                options={options}
                onChange={onChange}
                fetchOptions={fetchOptions}
                defaultOptions={options}
                {...props}
            />
        </div>
    );
};
